import { ContentType } from '../enums';
import { DescriptionType } from '../enums/DescriptionType';
import { Language } from '../enums/Language';
import { Description } from '../types/Description';
import { MultiLanguageTextModel } from './MultiLanguageTextModel';
import sanitizeHtml from 'sanitize-html';

const COLLECTIVE_DESCRIPTION_BODY_TYPES: DescriptionType[] = [
  DescriptionType.EventHeader,
  DescriptionType.EventHeaderShort,
  //DescriptionType.TeaserTooLong,
  DescriptionType.MainDescription,
  DescriptionType.MainDescription2,
  DescriptionType.AdditionalInformation,
  DescriptionType.GoodToKnow,
  DescriptionType.AdditionalServiceFAQGoodToKnow,
  DescriptionType.Difficulty,
  DescriptionType.AdditionalServiceSuitableFor,
  DescriptionType.AdditionalServiceSuitableFor2,
  DescriptionType.AdditionalServiceRequirements,
  DescriptionType.AdditionalServiceRequirements2,
  DescriptionType.AdditionalServiceEquipment,
  DescriptionType.AdditionalServiceEquipment2,
  DescriptionType.AdditionalServiceRentalEquipment,
  DescriptionType.AdditionalServiceMeetingPoint,
  DescriptionType.AdditionalServiceArrivalInformation,
  DescriptionType.AdditionalServiceArrivalInformation2,
  DescriptionType.AdditionalServiceOpeningHours,
  DescriptionType.AdditionalServiceIncludedServices2,
  DescriptionType.AdditionalServicePriceInformation,
  DescriptionType.AdditionalServicePriceInformation2,
  DescriptionType.CurrentInformation,
  DescriptionType.FeratelLocal,
  //DescriptionType.VenueTextualInfo, - goes in location section
  //DescriptionType.VenueDescription, - goes in location section
  // DescriptionType.Teaser, - goes in teaser section
  // DescriptionType.Venues, - goes in location section
  // DescriptionType.Venues2, - goes in location section
  // DescriptionType.LegalContact, - goes in contact section
  // DescriptionType.LegalContact2, - goes in contact section
  // DescriptionType.TimeInformation, - goes in time section
];

function getTeaserDescription(description: Description): Description {
  /*const teaser = description.content;
  if (teaser.length > 200) {
    return {
      ...description,
      type: DescriptionType.TeaserTooLong,
    };
  }*/
  return description;
}

const DESCRIPTION_TRANSFORMATIONS: {
  [key in DescriptionType]?: (description: Description) => Description;
} = {
  [DescriptionType.Teaser]: getTeaserDescription,
};

export class DescriptionModel {
  private _descriptions: Description[];
  private _typeToMultiLanguageText: Map<
    DescriptionType,
    MultiLanguageTextModel
  > = new Map();
  constructor(descriptions: Description[]) {
    this._descriptions = descriptions;
    this._typeToMultiLanguageText =
      this._createTypeToMultiLanguageTextMap(descriptions);
  }

  get descriptions() {
    return this._descriptions;
  }

  private _createTypeToMultiLanguageTextMap(
    descriptions: Description[]
  ): Map<DescriptionType, MultiLanguageTextModel> {
    const map = new Map();

    descriptions.forEach((d) => {
      const transformation = DESCRIPTION_TRANSFORMATIONS[d.type];
      if (transformation) {
        d = transformation(d);
      }
      if (!map.has(d.type)) {
        map.set(d.type, new MultiLanguageTextModel());
      }
      const multiLanguageText = map.get(d.type);
      const sanitized =
        d.contentType === ContentType.HTML
          ? sanitizeHtml(d.content, {
              allowedTags: [
                'b',
                'i',
                'em',
                'strong',
                'a',
                'p',
                'ul',
                'li',
                'ol',
                'br',
                'table',
                'tr',
                'td',
              ],
            })
          : d.content;
      multiLanguageText?.addText(d.languageIso, sanitized, d.contentType);
    });
    const collectiveDescriptionBody = new MultiLanguageTextModel();

    for (const descriptionType of COLLECTIVE_DESCRIPTION_BODY_TYPES) {
      const description = map.get(descriptionType);
      if (description) {
        collectiveDescriptionBody.add(description);
      }
    }

    map.set(
      DescriptionType.CollectiveDescriptionBody,
      collectiveDescriptionBody
    );
    return map;
  }

  public static from(data: DescriptionModel): DescriptionModel {
    return new DescriptionModel(data._descriptions);
  }

  getDescription(type: DescriptionType, languageIso: Language): string {
    return this.getMultiLanguageDescription(type)?.getText(languageIso) || '';
  }

  getMultiLanguageDescription(
    type: DescriptionType
  ): MultiLanguageTextModel | null {
    if (!this._typeToMultiLanguageText.has(type)) return null;

    return this._typeToMultiLanguageText.get(type)!;
  }
}
